// @ts-nocheck
/**
 * ツインキーシステム - バトル画面
 * @module twinKey/ui/screens/BattleScreen
 */

import React, { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { useTwinKeyContext } from '../../context/TwinKeyContext';
import { useBattle, useDungeon } from '../../hooks';
import { BATTLE_STATUS } from '../../core/battle';
import styles from '../styles/twinKeyStyles';
import DropManager from '../../content/drops/DropManager';
import {
  ATTRIBUTE_MATERIALS,
  CATEGORY_MATERIALS,
  GENERIC_MATERIALS,
  RARITY_FRAGMENTS,
  EXP_ORB_MATERIALS,
} from '../../content/alchemy/data/materialTypes';

/**
 * 素材IDまたは日本語名から素材情報を取得
 */
function getMaterialByIdOrName(materialIdOrName) {
  const allMaterials = {
    ...ATTRIBUTE_MATERIALS,
    ...CATEGORY_MATERIALS,
    ...GENERIC_MATERIALS,
    ...RARITY_FRAGMENTS,
    ...EXP_ORB_MATERIALS,
  };

  // 英語IDで検索
  if (allMaterials[materialIdOrName]) {
    return allMaterials[materialIdOrName];
  }

  // 日本語名で検索
  for (const mat of Object.values(allMaterials)) {
    if (mat.name === materialIdOrName) {
      return mat;
    }
  }

  // 見つからない場合はデフォルト（日本語名をそのまま表示）
  return { id: materialIdOrName, name: materialIdOrName, icon: '📦', tier: 'common' };
}

// CSSアニメーションをinject
const injectBattleAnimations = () => {
  if (typeof document === 'undefined') return;
  const styleId = 'battle-animations';
  if (document.getElementById(styleId)) return;

  const styleEl = document.createElement('style');
  styleEl.id = styleId;
  styleEl.textContent = styles.battleAnimationKeyframes;
  document.head.appendChild(styleEl);
};
injectBattleAnimations();

/**
 * バトル画面
 */
export function BattleScreen() {
  const { state, actions } = useTwinKeyContext();
  const {
    battleState,
    currentUnit,
    validTargets,
    waitingForInput,
    selectedSkill,
    setSelectedSkill,
    selectedTarget,
    setSelectedTarget,
    isAuto,
    speed,
    isAnimating,
    lastActionResult, // 演出用アクション結果
    attack,
    useSkill: activateSkill,
    defend,
    escape,
    toggleAuto,
    changeSpeed,
    executeNextAction,
    resetBattle,
  } = useBattle();
  const { completeCurrentFloor } = useDungeon();

  // ドロップマネージャー
  const dropManager = useMemo(() => new DropManager(), []);

  // 報酬を保存（currentBattleがnullになる前に保存）
  const savedRewardsRef = useRef(null);
  // 素材ドロップを保存（stateで管理して再レンダリングをトリガー）
  const [calculatedDrops, setCalculatedDrops] = useState([]);

  useEffect(() => {
    if (state.currentBattle?.rewards) {
      savedRewardsRef.current = state.currentBattle.rewards;
      console.log('[DEBUG BattleScreen] 報酬を保存:', savedRewardsRef.current);
    }
  }, [state.currentBattle]);

  // バトル勝利時に素材ドロップを計算
  useEffect(() => {
    if (battleState?.status === BATTLE_STATUS.VICTORY && calculatedDrops.length === 0) {
      const dungeon = state.currentDungeon;
      const continentId = dungeon?.continentId || 'unknown';
      const depthRatio = (dungeon?.currentFloor || 1) / Math.max(1, dungeon?.maxFloor || 10);
      const battleType = battleState.battleType || 'normal';
      const rawEnemies = battleState.enemies || [];

      // バトルユニットからcardId/categoryを抽出（sourceDataから取得）
      const enemies = rawEnemies.map(e => ({
        ...e,
        cardId: e.cardId || e.sourceData?.cardId,
        category: e.category || e.sourceData?.category,
        name: e.name || e.sourceData?.name,
      }));

      console.log('[DEBUG BattleScreen] 素材ドロップ計算開始:', { continentId, depthRatio, battleType });
      console.log('[DEBUG BattleScreen] enemies:', enemies.map(e => ({ name: e.name, cardId: e.cardId, category: e.category })));

      let materials = [];
      if (enemies.length > 0 && enemies.some(e => e.cardId || e.category)) {
        materials = dropManager.generateMultiEnemyDrops({
          enemies,
          continentId,
          depthRatio,
          battleType,
        });
      } else {
        materials = dropManager.generateBattleDrops({
          continentId,
          depthRatio,
          battleType,
          enemies,
        });
      }

      console.log('[DEBUG BattleScreen] 素材ドロップ結果:', materials);
      setCalculatedDrops(materials);
    }
  }, [battleState?.status, battleState?.enemies, battleState?.battleType, state.currentDungeon, dropManager, calculatedDrops.length]);

  // バトル終了後の続行処理
  const handleContinue = useCallback(() => {
    if (!battleState) return;

    const isVictory = battleState.status === BATTLE_STATUS.VICTORY;
    const isEscaped = battleState.status === BATTLE_STATUS.ESCAPED;

    // バトル後のパーティHP状態を収集（completeCurrentFloorに渡す用）
    // ※ React状態更新は非同期なので、直接渡さないと反映されない
    const allyHpStatus = [];
    let updatedParty = state.activeParty;

    if (state.activeParty && battleState.allies) {
      const updatedMembers = state.activeParty.members.map((member, index) => {
        // 対応するバトルユニットを探す（インデックス、uniqueId、名前の順で照合）
        let battleUnit = battleState.allies[index]; // まずインデックスで照合
        if (!battleUnit || battleUnit.instanceId !== member.uniqueId) {
          // インデックスが合わない場合はIDで探す
          battleUnit = battleState.allies.find(ally =>
            ally.instanceId === member.uniqueId || ally.name === member.name
          );
        }
        if (battleUnit) {
          console.log(`[HP同期] ${member.name}: ${member.stats?.currentHp} -> ${battleUnit.stats.currentHp}, isAlive: ${battleUnit.isAlive}`);
          // HP状態を収集
          allyHpStatus.push({
            uniqueId: member.uniqueId,
            cardId: member.cardId,
            name: member.name,
            currentHp: battleUnit.stats.currentHp,
            isAlive: battleUnit.isAlive,
          });
          return {
            ...member,
            stats: {
              ...member.stats,
              currentHp: battleUnit.stats.currentHp,
            },
            isAlive: battleUnit.isAlive,
          };
        }
        return member;
      });

      updatedParty = {
        ...state.activeParty,
        members: updatedMembers,
      };
      actions.setActiveParty(updatedParty);
    }

    // バトル状態をリセット
    resetBattle();

    if (isVictory) {
      // 勝利：フロア完了して次へ
      // 報酬は savedRewardsRef から取得（currentBattle は既に null の可能性がある）
      const rewards = savedRewardsRef.current || state.currentBattle?.rewards || {};
      const expToAward = rewards.exp || 0;
      // 事前計算済みのドロップを渡す
      const preCalculatedDrops = calculatedDrops || [];
      console.log('[DEBUG handleContinue] savedRewardsRef.current:', savedRewardsRef.current);
      console.log('[DEBUG handleContinue] expToAward:', expToAward);
      console.log('[DEBUG handleContinue] preCalculatedDrops:', preCalculatedDrops);
      console.log('[DEBUG handleContinue] allyHpStatus:', allyHpStatus);
      completeCurrentFloor({
        type: 'battle_won',
        gold: rewards.gold || 0,
        exp: expToAward,
        damageDealt: battleState.stats.totalDamageDealt,
        damageTaken: battleState.stats.totalDamageReceived,
        enemies: battleState.enemies || [],
        battleType: battleState.battleType || 'normal',
        preCalculatedDrops,  // 事前計算済みドロップを渡す
        allyHpStatus,        // 戦闘後HP状態を渡す
      });
      // ドロップをリセット
      setCalculatedDrops([]);
      // 画面遷移は completeCurrentFloor 内で処理される
      // - ダンジョン完了時: endDungeon() で 'result' 画面へ
      // - 継続時: 次のフロア選択肢が設定されるので 'dungeon' へ遷移が必要
      // completeCurrentFloor の戻り値または dungeon 状態で判断
    } else if (isEscaped) {
      // 逃走：フロア完了（報酬なし）して次へ
      completeCurrentFloor({
        type: 'escaped',
        allyHpStatus,  // 逃走時もHP状態を渡す
      });
      // 逃走時は継続なので dungeon へ
    } else {
      // 敗北：ダンジョン終了
      // completeCurrentFloor 内で endDungeon() が呼ばれ 'result' 画面へ
      completeCurrentFloor({
        type: 'battle_lost',
        allyHpStatus,  // 敗北時もHP状態を渡す
      });
    }
  }, [battleState, state.currentBattle, state.activeParty, completeCurrentFloor, actions, resetBattle, calculatedDrops, setCalculatedDrops]);

  if (!battleState) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyMessage}>
          バトル情報がありません
        </div>
      </div>
    );
  }

  const isBattleOngoing = battleState.status === BATTLE_STATUS.ONGOING;

  return (
    <div style={styles.battleContainer}>
      {/* バトルヘッダー */}
      <div style={styles.battleHeader}>
        <div style={styles.battleInfo}>
          <span style={styles.turnCounter}>ターン {battleState.turnCount}</span>
          <span style={styles.battleType}>
            {battleState.battleType === 'boss' ? '👑 ボス戦' :
             battleState.battleType === 'strong' ? '💀 強敵戦' : '⚔️ 通常戦'}
          </span>
        </div>
        <div style={styles.battleControls}>
          <button
            style={{
              ...styles.controlButton,
              ...(isAuto ? styles.activeControl : {}),
            }}
            onClick={toggleAuto}
          >
            {isAuto ? '🤖 AUTO' : '✋ MANUAL'}
          </button>
          <div style={styles.speedControls}>
            {[1, 2, 3].map(s => (
              <button
                key={s}
                style={{
                  ...styles.speedButton,
                  ...(speed === s ? styles.activeSpeed : {}),
                }}
                onClick={() => changeSpeed(s)}
              >
                x{s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 行動順表示 */}
      <TurnOrderBar
        turnOrder={battleState.turnOrder}
        allies={battleState.allies}
        enemies={battleState.enemies}
        currentIndex={battleState.currentTurnIndex}
      />

      {/* 敵エリア */}
      <div style={styles.enemyArea}>
        <div style={styles.enemyUnits}>
          {battleState.enemies.map((enemy, index) => (
            <BattleUnit
              key={enemy.id}
              unit={enemy}
              isEnemy={true}
              isCurrent={currentUnit?.id === enemy.id}
              isValidTarget={validTargets.some(t => t.id === enemy.id)}
              isSelected={selectedTarget === enemy.id}
              onClick={() => handleTargetSelect(enemy.id)}
              lastActionResult={lastActionResult}
            />
          ))}
        </div>
      </div>

      {/* 中央バトルログ */}
      <div style={styles.battleLogArea}>
        <BattleLog logs={battleState.logs.slice(-5)} />
      </div>

      {/* アクションバナー（演出用、1倍速のみ表示） */}
      {lastActionResult && speed === 1 && (
        <ActionBanner actionResult={lastActionResult} />
      )}

      {/* 味方エリア */}
      <div style={styles.allyArea}>
        <div style={styles.allyUnits}>
          {battleState.allies.map((ally, index) => (
            <BattleUnit
              key={ally.id}
              unit={ally}
              isEnemy={false}
              isCurrent={currentUnit?.id === ally.id}
              isValidTarget={false}
              isSelected={false}
              lastActionResult={lastActionResult}
            />
          ))}
        </div>
      </div>

      {/* アクションパネル（手動操作時） */}
      {isBattleOngoing && waitingForInput && currentUnit?.isAlly && (
        <ActionPanel
          unit={currentUnit}
          selectedSkill={selectedSkill}
          setSelectedSkill={setSelectedSkill}
          selectedTarget={selectedTarget}
          validTargets={validTargets}
          onAttack={attack}
          onSkill={activateSkill}
          onDefend={defend}
          onEscape={escape}
          isAnimating={isAnimating}
        />
      )}

      {/* バトル結果（終了時） */}
      {!isBattleOngoing && (
        <>
          {console.log('[DEBUG BattleScreen] savedRewardsRef.current:', savedRewardsRef.current)}
          {console.log('[DEBUG BattleScreen] calculatedDrops:', calculatedDrops)}
          <BattleResult
            status={battleState.status}
            stats={battleState.stats}
            rewards={savedRewardsRef.current}
            drops={calculatedDrops}
            onContinue={handleContinue}
          />
        </>
      )}
    </div>
  );

  // ターゲット選択ハンドラ
  function handleTargetSelect(targetId) {
    if (!waitingForInput) return;
    if (!validTargets.some(t => t.id === targetId)) return;

    setSelectedTarget(targetId);

    // スキル選択済みなら実行
    if (selectedSkill) {
      activateSkill(selectedSkill.id, targetId);
    } else {
      // 通常攻撃
      attack(targetId);
    }
  }
}

/**
 * 行動順バー
 */
function TurnOrderBar({ turnOrder, allies, enemies, currentIndex }) {
  const allUnits = [...allies, ...enemies];

  return (
    <div style={styles.turnOrderBar}>
      {turnOrder.slice(currentIndex, currentIndex + 7).map((unitId, idx) => {
        const unit = allUnits.find(u => u.id === unitId);
        if (!unit) return null;

        const attrColor = styles.getAttributeColor(unit.attribute);

        return (
          <div
            key={unitId}
            style={{
              ...styles.turnOrderUnit,
              ...(idx === 0 ? styles.currentTurnUnit : {}),
              borderColor: attrColor,
              opacity: unit.isAlive ? 1 : 0.3,
            }}
          >
            <div
              style={{
                ...styles.turnOrderIcon,
                background: unit.isAlly ? '#4CAF50' : '#f44336',
              }}
            >
              {unit.isAlly ? '⚔️' : '👹'}
            </div>
            <div style={styles.turnOrderName}>
              {unit.name.length > 4 ? unit.name.slice(0, 4) + '…' : unit.name}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * バトルユニット
 */
function BattleUnit({
  unit,
  isEnemy,
  isCurrent,
  isValidTarget,
  isSelected,
  onClick,
  lastActionResult,
}) {
  const attrColor = styles.getAttributeColor(unit.attribute);
  const hpPercent = (unit.stats.currentHp / unit.stats.maxHp) * 100;
  const hpColor = styles.getHpColor(hpPercent);

  // 演出用の状態判定
  const isAttacker = lastActionResult?.actor?.id === unit.id;
  const targetResult = lastActionResult?.results?.find(r => r.targetId === unit.id);
  const isTarget = !!targetResult;
  const isDefendAction = lastActionResult?.type === 'defend' && isAttacker;

  // ハイライトスタイルを決定
  let highlightStyle = {};
  if (isAttacker && !isDefendAction) {
    highlightStyle = styles.unitHighlightAttacker;
  } else if (isTarget) {
    highlightStyle = styles.unitHighlightTarget;
  } else if (isDefendAction) {
    highlightStyle = styles.unitHighlightDefend;
  }

  return (
    <div
      style={{
        ...styles.battleUnit,
        ...(isEnemy ? styles.enemyUnit : styles.allyUnit),
        ...(isCurrent ? styles.currentUnit : {}),
        ...(isValidTarget ? styles.validTargetUnit : {}),
        ...(isSelected ? styles.selectedUnit : {}),
        ...highlightStyle,
        borderColor: highlightStyle.borderColor || attrColor,
        opacity: unit.isAlive ? 1 : 0.4,
        transition: 'all 0.2s ease',
      }}
      onClick={onClick}
    >
      {/* ダメージ/回復フロート */}
      {isTarget && targetResult?.damage > 0 && (
        <div
          style={{
            ...styles.damageFloat,
            ...(targetResult?.isCritical ? styles.damageFloatCritical : {}),
          }}
        >
          -{targetResult.damage}
          {targetResult?.isCritical && ' CRITICAL!'}
        </div>
      )}
      {isTarget && targetResult?.heal > 0 && (
        <div style={styles.healFloat}>
          +{targetResult.heal}
        </div>
      )}

      {/* 属性アイコン */}
      <div
        style={{
          ...styles.unitAttrBadge,
          background: attrColor,
        }}
      >
        {styles.getAttributeIcon(unit.attribute)}
      </div>

      {/* ユニット名 */}
      <div style={styles.unitName}>{unit.name}</div>

      {/* HPバー */}
      <div style={styles.unitHpBarContainer}>
        <div
          style={{
            ...styles.unitHpBar,
            width: `${hpPercent}%`,
            background: hpColor,
          }}
        />
      </div>
      <div style={styles.unitHpText}>
        {unit.stats.currentHp} / {unit.stats.maxHp}
      </div>

      {/* ステータスアイコン */}
      <div style={styles.unitStatusIcons}>
        {unit.isDefending && <span style={styles.statusIcon}>🛡️</span>}
        {isCurrent && <span style={styles.currentIcon}>▶</span>}
      </div>

      {/* 死亡表示 */}
      {!unit.isAlive && (
        <div style={styles.deadOverlay}>💀</div>
      )}
    </div>
  );
}

/**
 * アクションパネル
 */
function ActionPanel({
  unit,
  selectedSkill,
  setSelectedSkill,
  selectedTarget,
  validTargets,
  onAttack,
  onSkill,
  onDefend,
  onEscape,
  isAnimating,
}) {
  return (
    <div style={styles.actionPanel}>
      <div style={styles.actionPanelHeader}>
        <span style={styles.actionUnitName}>{unit.name}</span> の行動
      </div>

      <div style={styles.actionButtons}>
        {/* 通常攻撃 */}
        <button
          style={{
            ...styles.actionButton,
            ...(!selectedSkill ? styles.selectedAction : {}),
          }}
          onClick={() => setSelectedSkill(null)}
          disabled={isAnimating}
        >
          ⚔️ 攻撃
        </button>

        {/* スキル */}
        {unit.skills?.map((skill, index) => {
          const isOnCooldown = skill.currentCd > 0;
          const isLocked = skill.unlocked === false;
          const isDisabled = isOnCooldown || isLocked;
          return (
            <button
              key={skill.id || index}
              style={{
                ...styles.actionButton,
                ...(selectedSkill?.id === skill.id ? styles.selectedAction : {}),
                ...(isDisabled ? styles.disabledAction : {}),
              }}
              onClick={() => !isDisabled && setSelectedSkill(skill)}
              disabled={isAnimating || isDisabled}
            >
              {isLocked ? '🔒 ' : ''}{skill.name || `スキル${index + 1}`}
              {isOnCooldown && !isLocked && ` (${skill.currentCd}T)`}
              {isLocked && ` (Lv${skill.unlockLevel || '?'})`}
            </button>
          );
        })}

        {/* 防御 */}
        <button
          style={styles.actionButton}
          onClick={onDefend}
          disabled={isAnimating}
        >
          🛡️ 防御
        </button>

        {/* 逃走 */}
        <button
          style={{
            ...styles.actionButton,
            ...styles.escapeButton,
          }}
          onClick={onEscape}
          disabled={isAnimating}
        >
          🚪 逃走
        </button>
      </div>

      {/* ターゲット選択ヒント */}
      {validTargets.length > 0 && (
        <div style={styles.targetHint}>
          敵を選択してください
        </div>
      )}
    </div>
  );
}

/**
 * バトルログ
 */
function BattleLog({ logs }) {
  return (
    <div style={styles.battleLog}>
      {logs.map((log, index) => (
        <div
          key={index}
          style={{
            ...styles.logEntry,
            ...getLogStyle(log.type),
          }}
        >
          {log.message}
        </div>
      ))}
    </div>
  );
}

/**
 * ログタイプに応じたスタイル
 */
function getLogStyle(type) {
  const styleMap = {
    damage: { color: '#ff6b6b' },
    heal: { color: '#6bff6b' },
    skill: { color: '#6b9eff' },
    enemyDefeat: { color: '#9d4ce6' },  // 敵を倒した時
    turn: { color: '#ffd700' },
    victory: { color: '#4CAF50', fontWeight: 'bold' },
    defeat: { color: '#f44336', fontWeight: 'bold' },  // 味方が倒された/敗北
    escape: { color: '#ff9500' },
  };
  return styleMap[type] || {};
}

/**
 * アクションバナー（中央表示の演出）
 */
function ActionBanner({ actionResult }) {
  if (!actionResult) return null;

  const { type, actor, targets, results, skillName } = actionResult;

  // アクションタイプに応じたアイコン
  const getActionIcon = () => {
    switch (type) {
      case 'attack': return '⚔️';
      case 'skill': return '✨';
      case 'defend': return '🛡️';
      default: return '⚡';
    }
  };

  // アクションテキスト
  const getActionText = () => {
    switch (type) {
      case 'attack': return 'の攻撃！';
      case 'skill': return skillName ? `「${skillName}」発動！` : 'のスキル！';
      case 'defend': return 'は防御態勢！';
      default: return '';
    }
  };

  // ターゲット名（複数対応）
  const targetNames = targets?.map(t => t.name).join(', ') || '';

  // 合計ダメージ
  const totalDamage = results?.reduce((sum, r) => sum + (r.damage || 0), 0) || 0;
  const hasCritical = results?.some(r => r.isCritical);
  const hasDefeat = results?.some(r => r.defeated);

  // 攻撃者の属性色
  const actorColor = styles.getAttributeColor(actor?.attribute || 'なし');

  return (
    <div style={styles.actionBannerOverlay}>
      <div style={styles.actionBanner}>
        {/* 攻撃者 */}
        <div style={{ ...styles.actionBannerActor, color: actorColor }}>
          {getActionIcon()} {actor?.name}
        </div>

        {/* 攻撃テキスト */}
        <div style={{ fontSize: '14px', color: '#a0a0b0', marginBottom: '4px' }}>
          {getActionText()}
        </div>

        {/* ターゲット（攻撃/スキル時） */}
        {(type === 'attack' || type === 'skill') && targetNames && (
          <>
            <div style={styles.actionBannerArrow}>↓</div>
            <div style={styles.actionBannerTarget}>{targetNames}</div>
          </>
        )}

        {/* ダメージ表示 */}
        {totalDamage > 0 && (
          <div
            style={hasCritical ? styles.actionBannerDamageCritical : styles.actionBannerDamage}
          >
            💥 {totalDamage} ダメージ！
            {hasCritical && ' (CRITICAL!)'}
          </div>
        )}

        {/* 撃破表示 */}
        {hasDefeat && (
          <div style={styles.actionBannerDefeat}>
            💀 撃破！
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * バトル結果
 */
function BattleResult({ status, stats, rewards, drops, onContinue }) {
  const isVictory = status === BATTLE_STATUS.VICTORY;
  const isEscaped = status === BATTLE_STATUS.ESCAPED;

  // ドロップを素材IDでグループ化し、表示情報を取得
  const groupedDrops = (drops || []).reduce((acc, drop) => {
    const materialId = drop.item || drop.type;
    if (!acc[materialId]) {
      const materialInfo = getMaterialByIdOrName(materialId);
      acc[materialId] = {
        ...materialInfo,
        count: 0,
      };
    }
    acc[materialId].count += drop.count;
    return acc;
  }, {});

  return (
    <div style={styles.battleResultOverlay}>
      <div style={styles.battleResultContent}>
        <div
          style={{
            ...styles.resultTitle,
            color: isVictory ? '#4CAF50' : isEscaped ? '#ff9500' : '#f44336',
          }}
        >
          {isVictory ? '🎉 勝利！' : isEscaped ? '🚪 逃走成功' : '💀 敗北...'}
        </div>

        <div style={styles.resultStats}>
          <div style={styles.resultStatItem}>
            <span style={styles.resultStatLabel}>ターン数</span>
            <span style={styles.resultStatValue}>{stats.turnsElapsed}</span>
          </div>
          <div style={styles.resultStatItem}>
            <span style={styles.resultStatLabel}>与ダメージ</span>
            <span style={styles.resultStatValue}>{stats.totalDamageDealt}</span>
          </div>
          <div style={styles.resultStatItem}>
            <span style={styles.resultStatLabel}>被ダメージ</span>
            <span style={styles.resultStatValue}>{stats.totalDamageReceived}</span>
          </div>
          <div style={styles.resultStatItem}>
            <span style={styles.resultStatLabel}>スキル使用</span>
            <span style={styles.resultStatValue}>{stats.skillsUsed}</span>
          </div>
          <div style={styles.resultStatItem}>
            <span style={styles.resultStatLabel}>クリティカル</span>
            <span style={styles.resultStatValue}>{stats.criticalHits}</span>
          </div>
          {isVictory && rewards?.exp > 0 && (
            <div style={styles.resultStatItem}>
              <span style={styles.resultStatLabel}>獲得経験値</span>
              <span style={{ ...styles.resultStatValue, color: '#4CAF50' }}>+{rewards.exp} EXP</span>
            </div>
          )}
        </div>

        {/* 素材ドロップ表示 */}
        {isVictory && Object.keys(groupedDrops).length > 0 && (
          <div style={dropStyles.dropsSection}>
            <div style={dropStyles.dropsTitle}>⚗️ 獲得素材</div>
            <div style={dropStyles.dropsGrid}>
              {Object.entries(groupedDrops).map(([materialId, material]) => (
                <div key={materialId} style={dropStyles.dropItem}>
                  <span style={dropStyles.dropIcon}>{material.icon}</span>
                  <span style={dropStyles.dropName}>{material.name}</span>
                  <span style={dropStyles.dropCount}>x{material.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 続行ボタン */}
        <button
          style={{
            ...styles.primaryButton,
            marginTop: '20px',
            fontSize: '18px',
            padding: '12px 24px',
          }}
          onClick={onContinue}
        >
          {isVictory ? '次のフロアへ →' : isEscaped ? '探索を続ける →' : '結果を確認 →'}
        </button>
      </div>
    </div>
  );
}

// ドロップ表示用スタイル
const dropStyles = {
  dropsSection: {
    marginTop: '16px',
    padding: '12px',
    background: 'rgba(156, 39, 176, 0.15)',
    borderRadius: '8px',
    border: '1px solid rgba(156, 39, 176, 0.3)',
  },
  dropsTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#ce93d8',
    marginBottom: '8px',
  },
  dropsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  dropItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '4px',
    fontSize: '12px',
  },
  dropIcon: {
    fontSize: '14px',
  },
  dropName: {
    color: '#e1bee7',
  },
  dropCount: {
    color: '#4CAF50',
    fontWeight: 'bold',
  },
};

export default BattleScreen;
